// KIS Broker custom settings panel.
//
// Registered via customRegistry["kis-broker-panel"]. The unified settings
// manifest declares group kis_broker as kind=custom with ref.endpoint pointing
// at /api/admin/kis-credentials. This component owns its own fetch + save
// because the KIS row lives in the dedicated kis_credentials table, not the
// generic settings store.
//
// Shape: TWO credential rows — paper (모의투자) and live (실전투자). The
// runtime "which row gets used" decision is driven by the unified
// kis.trading_mode setting (off/paper/live), edited in the Session
// orchestrator group — NOT here. This panel only edits credentials and
// surfaces which row is currently active as a read-only status badge.
// endpoint_base is server-derived from the mode and shown read-only so
// operators don't mismatch paper creds with the live host. Secret fields
// are masked by the server and preserved on save via SECRET_SENTINEL.
//
// Visual language follows admin-design building blocks (Card / Button / Badge
// / Input) so the panel renders correctly in dark mode and stays consistent
// with the rest of the admin UI.

import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import type { Group } from '@/admin-design/_common/settings/types'
import { Badge, Button, Card, Input } from '@/admin-design/_common'
import { useCustomPanelForm } from '@/admin-design/_common/settings/useCustomPanelForm'

type Mode = 'paper' | 'live'
type TradingMode = 'off' | 'paper' | 'live'

interface Props {
  group: Group
  endpoint: string
  onDirtyChange?: (dirty: boolean) => void
}

interface SettingsKey {
  value: string
  encrypted: boolean
  masked: boolean
}

interface KISCredRow {
  app_key: SettingsKey
  app_secret: SettingsKey
  account_no: SettingsKey
  endpoint_base: string
  configured: boolean
}

interface KISPayload {
  paper: KISCredRow
  live: KISCredRow
  trading_mode: TradingMode
  endpoints: Record<Mode, string>
}

const SECRET_SENTINEL = '__unchanged__'

async function fetchKIS(endpoint: string): Promise<KISPayload> {
  const r = await fetch(endpoint, { credentials: 'include' })
  if (!r.ok) throw new Error(`${endpoint}: ${r.status}`)
  return r.json()
}

// Derives which credential row (paper/live) the broker actually consumes
// from the unified trading_mode tri-state. off → paper, so balance/quote
// calls keep working against the simulator even when auto-trade is off.
function credentialRowFor(tm: TradingMode): Mode {
  return tm === 'live' ? 'live' : 'paper'
}

export default function KISBrokerPanel({ group, endpoint, onDirtyChange }: Props) {
  const q = useQuery<KISPayload, Error>({
    queryKey: ['endpoint', endpoint],
    queryFn: () => fetchKIS(endpoint),
  })

  const [paperDirty, setPaperDirty] = useState(false)
  const [liveDirty, setLiveDirty] = useState(false)
  useEffect(() => {
    onDirtyChange?.(paperDirty || liveDirty)
  }, [paperDirty, liveDirty, onDirtyChange])

  if (q.isPending) {
    return (
      <Card>
        <Card.Body className="text-sm text-muted-foreground">
          Loading {group.title}…
        </Card.Body>
      </Card>
    )
  }
  if (q.error || !q.data) {
    return (
      <Card className="border-destructive-soft bg-destructive-soft">
        <Card.Body className="text-sm text-destructive-soft-foreground">
          Failed to load {group.title}: {String(q.error)}
        </Card.Body>
      </Card>
    )
  }

  const payload = q.data
  const activeCred = credentialRowFor(payload.trading_mode)

  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <Card.Title>{group.title}</Card.Title>
          {group.description && (
            <Card.Description>{group.description}</Card.Description>
          )}
        </Card.Header>
        <Card.Body>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-foreground">Trading mode:</span>
            <TradingModeBadge mode={payload.trading_mode} />
            <span className="text-xs text-muted-foreground">
              → broker uses <span className="font-mono">{activeCred}</span> credentials
            </span>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            모드 변경은{' '}
            <span className="font-medium text-foreground">
              Session orchestrator
            </span>{' '}
            그룹의{' '}
            <span className="font-mono">trading_mode</span> 드롭다운에서 합니다.
            off = 섀도우만 (paper 자격증명 읽기 전용으로 사용) / paper = 모의투자
            자동매매 / live = 실전투자 자동매매. 이 패널은 자격증명 편집만
            담당합니다.
          </p>
        </Card.Body>
      </Card>

      <CredPanel
        mode="paper"
        label="Paper (모의투자)"
        row={payload.paper}
        endpointURL={payload.endpoints.paper}
        isActive={activeCred === 'paper'}
        endpoint={endpoint}
        onDirtyChange={setPaperDirty}
      />
      <CredPanel
        mode="live"
        label="Live (실전투자)"
        row={payload.live}
        endpointURL={payload.endpoints.live}
        isActive={activeCred === 'live'}
        endpoint={endpoint}
        onDirtyChange={setLiveDirty}
      />
    </div>
  )
}

function TradingModeBadge({ mode }: { mode: TradingMode }) {
  const variant: 'success' | 'warning' | 'muted' =
    mode === 'live' ? 'success' : mode === 'paper' ? 'warning' : 'muted'
  const label =
    mode === 'live' ? 'live (실거래)' : mode === 'paper' ? 'paper (모의)' : 'off (섀도우)'
  return <Badge variant={variant}>{label}</Badge>
}

function CredPanel({
  mode,
  label,
  row,
  endpointURL,
  isActive,
  endpoint,
  onDirtyChange,
}: {
  mode: Mode
  label: string
  row: KISCredRow
  endpointURL: string
  isActive: boolean
  endpoint: string
  onDirtyChange?: (dirty: boolean) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const form = useCustomPanelForm<KISPayload, { appKey: string; appSecret: string; accountNo: string }>({
    endpoint,
    toDraft: () => ({
      appKey: SECRET_SENTINEL,
      appSecret: SECRET_SENTINEL,
      accountNo: SECRET_SENTINEL,
    }),
    isDirty: (_server, draft) =>
      draft.appKey !== SECRET_SENTINEL ||
      draft.appSecret !== SECRET_SENTINEL ||
      draft.accountNo !== SECRET_SENTINEL,
    toBody: (draft) => {
      const body: Record<string, string> = { mode }
      if (draft.appKey !== SECRET_SENTINEL) body.app_key = draft.appKey
      if (draft.appSecret !== SECRET_SENTINEL) body.app_secret = draft.appSecret
      if (draft.accountNo !== SECRET_SENTINEL) body.account_no = draft.accountNo
      return body
    },
    onDirtyChange,
  })

  // Convenience aliases (keep existing JSX diff minimal):
  const { draft, setDraft, dirty, reset } = form
  const appKey = draft.appKey
  const appSecret = draft.appSecret
  const accountNo = draft.accountNo
  const setAppKey = (v: string) => setDraft((d) => ({ ...d, appKey: v }))
  const setAppSecret = (v: string) => setDraft((d) => ({ ...d, appSecret: v }))
  const setAccountNo = (v: string) => setDraft((d) => ({ ...d, accountNo: v }))

  const handleSave = async () => {
    try {
      await form.save()
      setToast('Saved')
      setError(null)
      setTimeout(() => setToast(null), 1500)
    } catch (_e) {
      // form.error will be set by the hook
    }
  }

  // Test connection for THIS mode specifically. Issues /oauth2/tokenP
  // against the endpoint_base implied by mode, with the saved app_key +
  // secret. Does not touch the cached token the orchestrator uses.
  const test = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/admin/kis-credentials/test?mode=${mode}`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!r.ok) throw new Error(await r.text())
      return (await r.json()) as { ok: boolean; mode: string; latency_ms?: number; error?: string }
    },
    onSuccess: (res) => {
      if (res.ok) {
        setToast(`✓ OK (${res.latency_ms ?? '?'}ms)`)
        setError(null)
      } else {
        setToast(null)
        setError(`✗ ${res.error ?? 'unknown'}`)
      }
      setTimeout(() => setToast(null), 2500)
    },
    onError: (e) => setError(String(e)),
  })

  return (
    <Card>
      <Card.Header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Card.Title className="flex items-center gap-2 flex-wrap">
            <span>{label}</span>
            {isActive && <Badge variant="success">active</Badge>}
            {!row.configured && <Badge variant="warning">not configured</Badge>}
            {dirty && (
              <span className="text-xs font-normal text-warning">● unsaved</span>
            )}
          </Card.Title>
          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
            {endpointURL}
          </p>
        </div>
      </Card.Header>

      {(error || form.error) && (
        <div className="border-b border-destructive-soft bg-destructive-soft px-6 py-3 text-sm text-destructive-soft-foreground">
          {error || (form.error ? String(form.error.message) : '')}
        </div>
      )}

      <div className="divide-y divide-border">
        <SecretRow
          label="App Key"
          masked={row.app_key?.value ?? ''}
          draft={appKey}
          onChange={setAppKey}
        />
        <SecretRow
          label="App Secret"
          masked={row.app_secret?.value ?? ''}
          draft={appSecret}
          onChange={setAppSecret}
        />
        <SecretRow
          label="Account No"
          masked={row.account_no?.value ?? ''}
          draft={accountNo}
          onChange={setAccountNo}
        />
      </div>

      <Card.Footer className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          {toast
            ? toast
            : dirty
            ? '변경사항이 저장되지 않았습니다'
            : '변경사항 없음'}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!row.configured || test.isPending || dirty}
            loading={test.isPending}
            onClick={() => test.mutate()}
            title={
              dirty
                ? '변경사항을 먼저 저장한 뒤 테스트하세요'
                : row.configured
                ? '이 모드의 저장된 자격증명으로 KIS 토큰 발급을 시도합니다'
                : '자격증명 저장 후 테스트 가능'
            }
          >
            Test connection
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!dirty || form.isSaving}
            onClick={reset}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty || form.isSaving}
            loading={form.isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </Card.Footer>
    </Card>
  )
}

function SecretRow({
  label,
  masked,
  draft,
  onChange,
}: {
  label: string
  masked: string
  draft: string
  onChange: (v: string) => void
}) {
  const editing = draft !== SECRET_SENTINEL
  return (
    <div className="px-6 py-4">
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 md:grid-cols-[minmax(160px,240px)_1fr]">
        <label className="text-sm font-medium text-foreground md:pt-2">
          {label}
        </label>
        <div>
          {!editing ? (
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-foreground">
                {masked || <span className="text-muted-foreground">(not set)</span>}
              </span>
              <Button variant="secondary" size="sm" onClick={() => onChange('')}>
                Change
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                type="text"
                className="font-mono"
                value={draft}
                onChange={(e) => onChange(e.target.value)}
                autoFocus
              />
              <Button variant="secondary" size="sm" onClick={() => onChange(SECRET_SENTINEL)}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
